import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { createMyRequest, fetchMyRequests, fetchMyReservations } from '../api';

// Member "Request Services" panel. Members ask staff for things: schedule the
// cleaning crew for their checkout day, have the groundskeeper set up the clay
// range or bounce house, etc. They can optionally tie a request to one of their
// upcoming stays so staff know which spot / which day.

const REQUEST_TYPES = [
  { value: 'cleaning', label: 'Cleaning crew' },
  { value: 'groundskeeper', label: 'Groundskeeper' },
  { value: 'clay_range', label: 'Clay shooting range setup' },
  { value: 'bounce_house', label: 'Bounce house' },
  { value: 'other', label: 'Other' },
];

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
  declined: 'Declined',
};

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MemberRequests() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();

  const [requests, setRequests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ requestType: 'cleaning', details: '', requestedDate: '', reservationId: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, res] = await Promise.all([
        fetchMyRequests(instance, account),
        fetchMyReservations(instance, account),
      ]);
      if (reqs.ok) setRequests(reqs.data.requests || []);
      if (res.ok) setReservations(res.data.reservations || []);
    } finally {
      setLoading(false);
    }
  }, [instance, account]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (form.requestType === 'other' && !form.details.trim()) {
      setMsg({ type: 'error', text: 'Please describe your request.' });
      return;
    }
    setSaving(true);
    try {
      const res = await createMyRequest(instance, account, {
        requestType: form.requestType,
        details: form.details.trim() || undefined,
        requestedDate: form.requestedDate || undefined,
        reservationId: form.reservationId || undefined,
      });
      if (res.ok) {
        setMsg({ type: 'success', text: 'Request sent to the club. We\u2019ll follow up soon.' });
        setForm({ requestType: 'cleaning', details: '', requestedDate: '', reservationId: '' });
        await load();
      } else {
        setMsg({ type: 'error', text: res.data?.message || 'Could not send your request.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2>Request Services</h2>
      <p className="muted">Ask the club to schedule a cleaning crew, set up the clay range or bounce house, or anything else for your visit.</p>

      <form className="sched-form" onSubmit={submit}>
        <div className="form-row">
          <label>
            What do you need?
            <select value={form.requestType} onChange={(e) => setForm({ ...form, requestType: e.target.value })}>
              {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label>
            Date needed (optional)
            <input type="date" value={form.requestedDate}
              onChange={(e) => setForm({ ...form, requestedDate: e.target.value })} />
          </label>
        </div>
        {reservations.length > 0 && (
          <label>
            Related stay (optional)
            <select value={form.reservationId} onChange={(e) => setForm({ ...form, reservationId: e.target.value })}>
              <option value="">Not tied to a specific stay</option>
              {reservations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.spot_name}: {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Details
          <textarea rows={3} placeholder="Anything the crew or groundskeeper should know…"
            value={form.details} maxLength={2000}
            onChange={(e) => setForm({ ...form, details: e.target.value })} />
        </label>
        <button type="submit" disabled={saving}>{saving ? 'Sending…' : 'Send request'}</button>
        {msg && <span className={msg.type === 'error' ? 'error-text' : 'success-text'}>{msg.text}</span>}
      </form>

      <h3 className="sched-subhead">Your requests</h3>
      {loading ? <p className="muted">Loading…</p> : requests.length === 0 ? (
        <p className="muted">You haven't sent any requests yet.</p>
      ) : (
        <ul className="sched-list">
          {requests.map((r) => {
            const typeLabel = REQUEST_TYPES.find((t) => t.value === r.request_type)?.label || r.request_type;
            return (
              <li key={r.id} className="sched-item">
                <div>
                  <strong>{typeLabel}</strong>
                  <span className={`req-status req-${r.status}`}>{STATUS_LABELS[r.status] || r.status}</span>
                  {r.requested_date && <div className="muted">For {fmtDate(r.requested_date)}</div>}
                  {r.spot_name && <div className="muted">Stay: {r.spot_name}</div>}
                  {r.details && <div className="sched-note">{r.details}</div>}
                  {r.admin_notes && <div className="admin-note">Club: {r.admin_notes}</div>}
                </div>
                <span className="muted req-date">{fmtDate(r.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
